import { get, post, patch, del } from '../httpMethods';
import { ApiResponse, PaginationMeta } from '../../types/api';

export interface Doctor {
  id: string;
  name: string;
  employeeCode: string;
  type: string;
  qualifications?: string[];
  registrationNumber?: string;
  email?: string;
  phone?: string;
  isActive: boolean;
  departmentId?: string;
  designationId?: string;
  photoUrl?: string;
  specializationIds?: string[];
  revenueSharePercent: number;
  showOnWebsite?: boolean;
  consultationFeeNew?: number;
  consultationFeeFollowup?: number;
  consultationFeeReport?: number;
  consultationAvailability?: ConsultationAvailability[];
  consultationRoomId?: string;
  createdAt: string;
  updatedAt: string;
  department?: {
    id?: string;
    name: string;
    code?: string;
  };
  departments?: Array<{
    id: string;
    name: string;
    code?: string;
  }>;
  designation?: {
    name: string;
  };
  specializations?: Array<{
    id: string;
    label: string;
  }>;
  consultationRoom?: {
    id: string;
    name: string;
    floor?: {
      id?: string;
      name?: string;
      floorNumber?: number;
    };
  };
  user?: {
    id: string;
    username: string;
    email: string;
    firstName: string;
    lastName: string;
    roles: string[];
    isActive?: boolean;
    dashboardAccess?: boolean;
    lastLoginAt?: string;
    customRoles?: { id: string; name: string }[];
  };
}

export interface ConsultationTimeRange {
  title: string;
  from: string;
  to: string;
}

export interface ConsultationAvailability {
  day: string;
  timeRanges: ConsultationTimeRange[];
}

export interface CreateDoctorDto {
  name: string;
  employeeCode?: string;
  type?: string;
  qualifications?: string[];
  registrationNumber?: string;
  email?: string;
  phone?: string;
  isActive?: boolean;
  dashboardAccess?: boolean;
  departmentId?: string;
  departmentIds?: string[];
  designationId?: string;
  photoUrl?: string;
  specializationIds?: string[];
  revenueSharePercent?: number;
  showOnWebsite?: boolean;
  consultationFeeNew?: number;
  consultationFeeFollowup?: number;
  consultationFeeReport?: number;
  consultationAvailability?: ConsultationAvailability[];
  consultationRoomId?: string;
  // User account fields
  username?: string;
  password?: string;
  roles?: string[];
  customRoleIds?: string[];
}

export const doctorService = {
  getDoctors: (
    page = 1,
    limit = 20,
    filters?: { search?: string; departmentId?: string; designationId?: string; specializationId?: string; status?: string }
  ): Promise<ApiResponse<Doctor[]> & { meta: PaginationMeta }> => {
    const params = new URLSearchParams({ page: String(page), limit: String(limit) });
    if (filters?.search) params.set('search', filters.search);
    if (filters?.departmentId) params.set('departmentId', filters.departmentId);
    if (filters?.designationId) params.set('designationId', filters.designationId);
    if (filters?.specializationId) params.set('specializationId', filters.specializationId);
    if (filters?.status) params.set('status', filters.status);
    return get<Doctor[]>(`/doctors?${params.toString()}`) as Promise<ApiResponse<Doctor[]> & { meta: PaginationMeta }>;
  },

  getDoctor: (id: string): Promise<ApiResponse<Doctor>> => {
    return get<Doctor>(`/doctors/${id}`);
  },

  getNextCode: (): Promise<ApiResponse<{ code: string }>> => {
    return get<{ code: string }>('/doctors/next-code');
  },

  createDoctor: (data: CreateDoctorDto): Promise<ApiResponse<Doctor>> => {
    return post<Doctor>('/doctors', data);
  },

  updateDoctor: (id: string, data: Partial<CreateDoctorDto>): Promise<ApiResponse<Doctor>> => {
    return patch<Doctor>(`/doctors/${id}`, data);
  },

  deleteDoctor: (id: string): Promise<ApiResponse<void>> => {
    return del<void>(`/doctors/${id}`);
  },

  /** List soft-deleted doctors (the Trash page). */
  getTrashedDoctors: (
    page = 1,
    limit = 20,
    filters?: { search?: string; departmentId?: string; designationId?: string; specializationId?: string; status?: string }
  ): Promise<ApiResponse<Doctor[]> & { meta: PaginationMeta }> => {
    const params = new URLSearchParams({ page: String(page), limit: String(limit) });
    if (filters?.search) params.set('search', filters.search);
    if (filters?.departmentId) params.set('departmentId', filters.departmentId);
    if (filters?.designationId) params.set('designationId', filters.designationId);
    if (filters?.specializationId) params.set('specializationId', filters.specializationId);
    if (filters?.status) params.set('status', filters.status);
    return get<Doctor[]>(`/doctors/trash?${params.toString()}`) as Promise<ApiResponse<Doctor[]> & { meta: PaginationMeta }>;
  },

  /** Restore trashed doctors back to the active list. */
  restoreDoctors: (ids: string[]): Promise<ApiResponse<{ restored: string[]; notFound: string[]; blocked?: { id: string; reason: string }[] }>> => {
    return post<{ restored: string[]; notFound: string[]; blocked?: { id: string; reason: string }[] }>('/doctors/trash/bulk-restore', { ids });
  },

  /** Permanently delete trashed doctors. */
  permanentlyDeleteDoctors: (ids: string[]): Promise<ApiResponse<{ deleted: string[]; notFound: string[]; blocked?: { id: string; reason: string }[] }>> => {
    return post<{ deleted: string[]; notFound: string[]; blocked?: { id: string; reason: string }[] }>('/doctors/trash/bulk-delete', { ids });
  },

  /**
   * Permanently delete trashed doctors together with every record that
   * references them (appointments, billing, revenue, attendance, linked login,
   * etc.). Use when the normal permanent delete is blocked by linked records.
   */
  forceDeleteDoctors: (
    ids: string[],
  ): Promise<
    ApiResponse<{
      deleted: string[];
      notFound: string[];
      purged: Record<string, number>;
      blocked?: { id: string; reason: string }[];
    }>
  > => {
    return post<{
      deleted: string[];
      notFound: string[];
      purged: Record<string, number>;
      blocked?: { id: string; reason: string }[];
    }>('/doctors/trash/bulk-force-delete', { ids });
  },
};
